/**
 * Pure anon-Hunt identity constants — NO `server-only`, so the edge middleware
 * (which mints the cookie) and the server-only session store (which reads it)
 * can both import them. KV access + quota live in the server-only
 * `anon-session.ts`.
 */

/** 24h — anon state is ephemeral; conversion to signup carries it forward. */
export const ANON_TTL_SECONDS = 24 * 60 * 60;

/**
 * HttpOnly identity cookie holding the anon session UUID — the KV key. HttpOnly
 * so client JS can't read or forge it; the server mints it (middleware) and
 * reads it (Hunt actions). Distinct from the non-HttpOnly *signal* cookies
 * (`hunt_interest_v1`, `ld_recent_jobs_v1`).
 */
export const HUNT_ANON_COOKIE = "hunt_anon_sid";

export const HUNT_ANON_COOKIE_OPTIONS = {
	maxAge: ANON_TTL_SECONDS,
	path: "/",
	sameSite: "lax" as const,
	httpOnly: true,
	secure: true,
} as const;

/**
 * `?src=` values that admit a logged-out visitor to the anon Hunt even when
 * the `hunt-anon` flag is off: the homepage hero's talent tab (`home_hero`
 * form, `home_hero_chip` suggestion chips), the welcome_d0 lifecycle email,
 * and the /jobs feed's Hunt entry card (`feed_hunt_card`). Read by the
 * /seeker page and by middleware, which records the admission in
 * {@link HUNT_ANON_ENTRY_COOKIE}.
 */
export const ANON_HUNT_ENTRY_SRCS: ReadonlySet<string> = new Set([
	"welcome_d0",
	"home_hero",
	"home_hero_chip",
	"feed_hunt_card",
]);

export function isAnonHuntEntrySrc(src: unknown): boolean {
	return typeof src === "string" && ANON_HUNT_ENTRY_SRCS.has(src);
}

/**
 * Set by middleware when a logged-out visitor lands on /seeker through an
 * {@link ANON_HUNT_ENTRY_SRCS} link. The page's admission used to stop at the
 * first render: the chat bootstrap and every later turn resolve the anon
 * actor in `conversation.ts`, which checked only `hunt-anon`, so with the flag
 * off an admitted guest was still redirected to /login. This cookie carries
 * the admission to those server actions. It grants nothing a visitor could
 * not get by adding `?src=feed_hunt_card` to the URL.
 */
export const HUNT_ANON_ENTRY_COOKIE = "hunt_anon_entry";

/**
 * UUID v4 only — the format the cookie carries. Rejects anything else so a
 * hostile `anonId` can't be used to probe KV with arbitrary keys.
 */
export function isValidAnonId(id: string | null | undefined): id is string {
	if (!id) return false;
	return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
		id,
	);
}

/** Server-side fresh anon session id (UUID v4, matches `isValidAnonId`). */
export function newAnonId(): string {
	return crypto.randomUUID();
}
